import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import * as React from 'react';
import { brandPrimary } from '../../../../styles/colors';
import { baseStyleSheet, bs } from '../../../../styles/styles';
import { DeprecatedPopover, PopoverType } from '../../../DeprecatedPopover';
import { QuestionMarkIcon } from '../../../svgs/icons/QuestionMarkIcon';
import { styleSheet } from './styles';

interface IProps {
	campaign?: Api.ICampaign;
	className?: string;
	onCancelClicked?(e: React.MouseEvent<HTMLElement, MouseEvent>): void;
	styles?: StyleDeclarationValue[];
}

interface ICampaignStat {
	count?: number;
	message?: string;
}

const PERCENTAGE_BASE = 100.0;

const calculatePercentage = (count: number, total: number): string => {
	return `${Math.ceil(PERCENTAGE_BASE * (count / total))} %`;
};

const getStats = (campaign?: Api.ICampaign): ICampaignStat[] => {
	if (campaign) {
		return campaign.completedDate
			? [
					{ count: campaign.sentSuccessfullyCount, message: 'emailed' },
					{ count: campaign.openCount, message: 'viewed' },
					// Show clicked has 0 even if the user has no clicks
					campaign.clickTrackingEnabled ? { count: campaign.distinctClickCount || 0, message: 'clicked' } : null,
					campaign.replyCount > 0 ? { count: campaign.replyCount, message: 'replied' } : null,
					campaign.unsubscribedCount !== -1 ? { count: campaign.unsubscribedCount || 0, message: "unsub'd" } : null,
					campaign.failedCount > 0 ? { count: campaign.failedCount, message: 'failed' } : null,
					campaign.noEmailAddressCount > 0 ? { count: campaign.noEmailAddressCount, message: 'no email' } : null,
				].filter(x => !!x)
			: [
					{
						count:
							campaign.totalEmails -
							campaign.sentSuccessfullyCount -
							campaign.failedCount -
							campaign.noEmailAddressCount,
						message: 'queued',
					},
					{
						count: campaign.sentSuccessfullyCount,
						message: 'emailed',
					},
					{
						count: campaign.openCount,
						message: 'viewed',
					},
					campaign.clickTrackingEnabled
						? {
								count: campaign.distinctClickCount || 0,
								message: 'clicked',
							}
						: null,
					campaign.unsubscribedCount !== -1 ? { count: campaign.unsubscribedCount || 0, message: "unsub'd" } : null,
					{
						count: campaign.failedCount,
						message: 'failed',
					},
					{
						count: campaign.noEmailAddressCount,
						message: 'no email',
					},
				].filter(x => !!x);
	}
	return null;
};
interface IGroupEmailTableRowAnalyticsComponent {
	getStats?(campaign?: Api.ICampaign): ICampaignStat[];
}

export const GroupEmailTableRowAnalytics: React.FC<IProps> & IGroupEmailTableRowAnalyticsComponent = props => {
	const { className, styles = [], campaign, onCancelClicked } = props;
	const [toggleShowingTooltip, setToggleShowingTooltip] = React.useState<boolean>(false);

	const stats = React.useMemo(() => {
		return GroupEmailTableRowAnalytics.getStats(campaign);
	}, [campaign]);
	return (
		<div
			className={`${css(styleSheet.container, bs.gap2, ...styles)} group-email-table-row-analytics ${className || ''}`}
		>
			{stats?.map(s => {
				const percentViewedClickedRepliedUnsubscribed =
					campaign.sentSuccessfullyCount > 0 &&
					(s.message === 'viewed' || s.message === 'clicked' || s.message === 'replied' || s.message === "unsub'd") &&
					s.count >= 0
						? calculatePercentage(s.count, campaign.sentSuccessfullyCount)
						: null;
				const percentFailed =
					campaign.totalEmails > 0 && s.message === 'failed' && s.count > 0
						? calculatePercentage(s.count, campaign.totalEmails)
						: null;
				return (
					<div
						key={s.message}
						title={
							percentFailed &&
							'The failed % is calculated from the total number of emails, not only those successfully sent.'
						}
						className={css(bs.p2)}
					>
						<div
							className={css(
								styleSheet.count,
								(s.message === 'failed' || s.message === "unsub'd") && s.count > 0 ? styleSheet.countFailed : null
							)}
						>
							{s.count}
						</div>
						{percentViewedClickedRepliedUnsubscribed && (
							<div className={css(styleSheet.action)}>{percentViewedClickedRepliedUnsubscribed}</div>
						)}
						{percentFailed && <div className={css(styleSheet.action)}>{percentFailed}</div>}
						<div className={css(styleSheet.action)}>
							{s.message}
							{s.message === 'viewed' && campaign.machineOpenObserved ? (
								<>
									<DeprecatedPopover
										anchor={
											<span>
												<QuestionMarkIcon
													className={css(styleSheet.questionMarkIcon)}
													fillColor={brandPrimary}
													onMouseEnter={() => setToggleShowingTooltip(true)}
													onMouseLeave={() => setToggleShowingTooltip(false)}
												/>
											</span>
										}
										className={css(styleSheet.popOver)}
										contentClassName={css(styleSheet.popOverContentContainer)}
										dismissOnClickOutside={false}
										isOpen={toggleShowingTooltip}
										place='below'
										preferredPlacement='right'
										tipSize={0.01}
										type={PopoverType.gray}
									>
										<p className={css(styleSheet.popOverContent)}>
											Due to privacy protection settings, we cannot be certain <br />
											if emails opened on Apple devices have been viewed.
										</p>
									</DeprecatedPopover>
								</>
							) : null}
						</div>
						{s.message === 'queued' && (
							<button className={css(baseStyleSheet.brandLink, styleSheet.cancelButton)} onClick={onCancelClicked}>
								<span>Cancel</span>
							</button>
						)}
					</div>
				);
			})}
		</div>
	);
};

GroupEmailTableRowAnalytics.getStats = getStats;
